import React, { useState, useEffect } from 'react';
import { 
  message, 
  Tag
} from 'antd';
import { 
  PlusOutlined, 
  ReloadOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  FilterOutlined,
  GiftOutlined,
  DownOutlined
} from '@ant-design/icons';
import { giftService, GiftGiver } from '../../../services/giftService';
import { GiftGiversDetailModalMobile } from '../../../components/givers/GiftGiversDetailModalMobile';
import { AddGiftsGiversModalMobile } from '../../../components/givers/AddGiftsGiversModalMobile';
import { FilterModalMobile } from '../../../components/givers/FilterModalMobile';
import styles from './GiftGiversPageMobile.module.css';

const GiftGiversPageMobile: React.FC = () => {
  const [, setGivers] = useState<GiftGiver[]>([]);
  const [giversWithStats, setGiversWithStats] = useState<GiftGiver[]>([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedGiver, setSelectedGiver] = useState<GiftGiver | null>(null);
  
  // Add/Edit modal states
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [editingGiverForModal, setEditingGiverForModal] = useState<GiftGiver | null>(null);
  const [addEditModalMode, setAddEditModalMode] = useState<'create' | 'edit'>('create');
  
  // Filter modal states
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({
    name: '',
    relationship: undefined as string | undefined,
    status: undefined as boolean | undefined
  });
  
  const filteredGivers = giversWithStats.filter(giver => {
    const nameMatch = !currentFilters.name || 
      giver.full_name.toLowerCase().includes(currentFilters.name.toLowerCase()) ||
      (giver.phone && giver.phone.includes(currentFilters.name)) ||
      (giver.email && giver.email.toLowerCase().includes(currentFilters.name.toLowerCase()));
    
    const relationshipMatch = !currentFilters.relationship || giver.relationship === currentFilters.relationship;
    const statusMatch = currentFilters.status === undefined || giver.is_active === currentFilters.status;
    
    return nameMatch && relationshipMatch && statusMatch;
  });



  const fetchGivers = async (reset = false) => {
    try {
      // Xác định trang cần load
      const pageToLoad = reset ? 1 : currentPage;
      
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // Prepare API parameters
      const apiParams: any = {
        page: pageToLoad,
        limit: pageSize,
      };

      // Add search filters if any
      if (currentFilters.name) {
        apiParams.search = currentFilters.name;
      }
      if (currentFilters.relationship) {
        apiParams.relationship = currentFilters.relationship;
      }
      if (currentFilters.status !== undefined) {
        apiParams.is_active = currentFilters.status;
      }

      console.log('API Parameters:', apiParams);

      const response = await giftService.getGiftGivers(apiParams);
      
      console.log('API Response:', response);

      // Cập nhật dữ liệu
      if (reset) {
        setGivers(response.data);
        setGiversWithStats(response.data);
        setCurrentPage(1);
      } else {
        // Merge data mới với data cũ, tránh trùng lặp
        setGivers(prev => {
          const existingIds = new Set(prev.map(giver => giver.id));
          const newGivers = response.data.filter(giver => !existingIds.has(giver.id));
          return [...prev, ...newGivers];
        });
        setGiversWithStats(prev => {
          const existingIds = new Set(prev.map(giver => giver.id));
          const newGivers = response.data.filter(giver => !existingIds.has(giver.id));
          return [...prev, ...newGivers];
        });
      }
      
      // Cập nhật pagination info
      const pagination = response.pagination;
      if (pagination) {
        setTotal(pagination.total);
        setHasMore(pageToLoad < pagination.totalPages);
        console.log('Has more:', pageToLoad < pagination.totalPages, 'Current page:', pageToLoad, 'Total pages:', pagination.totalPages);
      } else {
        setHasMore(response.data.length === pageSize);
        console.log('No pagination, has more based on data length:', response.data.length === pageSize);
      }
    } catch (error) {
      console.error('Error in fetchGivers:', error);
      message.error('Không thể tải danh sách người mừng');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const refreshData = () => {
    fetchGivers(true);
  };

  // Load more function
  const loadMore = () => {
    console.log('LoadMore called - loadingMore:', loadingMore, 'hasMore:', hasMore, 'loading:', loading);
    if (!loadingMore && hasMore && !loading) {
      console.log('Incrementing current page from', currentPage, 'to', currentPage + 1);
      setCurrentPage(prev => prev + 1);
    } else {
      console.log('LoadMore blocked - loadingMore:', loadingMore, 'hasMore:', hasMore, 'loading:', loading);
    }
  };

  // Throttle scroll để tránh gọi quá nhiều
  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    console.log('Scroll percentage:', scrollPercentage, 'Has more:', hasMore, 'Loading more:', loadingMore);
    
    // Trigger load more khi scroll đến 80%
    if (scrollPercentage > 0.8 && hasMore && !loadingMore && !loading) {
      console.log('Triggering load more...');
      loadMore();
    }
  }, [hasMore, loadingMore, loading]);

  // Effect để load dữ liệu khi page thay đổi
  useEffect(() => {
    console.log('useEffect triggered - currentPage:', currentPage, 'pageSize:', pageSize);
    if (currentPage === 1) {
      fetchGivers(true); // Reset data khi về trang 1
    } else {
      fetchGivers(false); // Load more khi trang > 1
    }
  }, [currentPage, pageSize]);

  // Effect để reset pagination khi filter thay đổi
  useEffect(() => {
    console.log('Filter effect triggered - currentFilters:', currentFilters);
    setCurrentPage(1);
    setHasMore(true);
    setGivers([]);
    setGiversWithStats([]);
    setTotal(0);
    // Fetch dữ liệu mới với filter mới
    setTimeout(() => {
      fetchGivers(true);
    }, 100);
  }, [currentFilters]);

  const handleCreate = () => {
    setEditingGiverForModal(null);
    setAddEditModalMode('create');
    setAddEditModalOpen(true);
  };

  const handleView = (record: GiftGiver) => {
    setSelectedGiver(record);
    setDetailModalOpen(true);
  };

  const handleEdit = (giver: GiftGiver) => {
    setEditingGiverForModal(giver);
    setAddEditModalMode('edit');
    setAddEditModalOpen(true);
    setDetailModalOpen(false);
  };

  const handleModalSuccess = () => {
    // Refresh data when there are changes
    setCurrentPage(1);
    setHasMore(true);
    setGivers([]);
    setGiversWithStats([]);
    setTotal(0);
    fetchGivers(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedGiver(null);
  };

  const handleAddEditModalClose = () => {
    setAddEditModalOpen(false);
    setEditingGiverForModal(null);
    setAddEditModalMode('create');
  };

  const handleFilterModalClose = () => {
    setFilterModalOpen(false);
  };

  const handleApplyFilters = (filters: typeof currentFilters) => {
    setCurrentFilters(filters);
  };

  const handleClearFilters = () => {
    setCurrentFilters({
      name: '',
      relationship: undefined,
      status: undefined
    });
  };

  const getFilterCount = () => {
    let count = 0;
    if (currentFilters.name) count++;
    if (currentFilters.relationship) count++;
    if (currentFilters.status !== undefined) count++;
    return count;
  };

  const getRelationshipLabel = (relationship: string) => {
    const labels: Record<string, string> = {
      family_internal: 'Gia đình nội',
      family_external: 'Gia đình ngoại',
      friend_bride: 'Bạn cô dâu',
      friend_groom: 'Bạn chú rể',
      colleague: 'Đồng nghiệp',
      business_partner: 'Đối tác',
      other: 'Khác'
    };
    return labels[relationship] || relationship;
  };

  const getRelationshipColor = (relationship: string) => {
    const colors: Record<string, string> = {
      family_internal: 'red',
      family_external: 'orange',
      friend_bride: 'pink',
      friend_groom: 'blue',
      colleague: 'green',
      business_partner: 'purple',
      other: 'default'
    };
    return colors[relationship] || 'default';
  };



  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) => 
      regex.test(part) ? <span key={index} className={styles.highlight}>{part}</span> : part
    );
  };

  const renderGiverCard = (giver: any) => (
    <div 
      key={giver.id} 
      className={styles.giverCard}
      onClick={() => handleView(giver)}
    >
      <div className={styles.iconContainer}>
        <GiftOutlined />
      </div>
      
      <div className={styles.giverDetails}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <h3 className={styles.giverName}>
            {highlightText(giver.full_name, currentFilters.name)}
          </h3>
          <Tag 
            color={getRelationshipColor(giver.relationship)}
            className={styles.relationshipTag}
          >
            {getRelationshipLabel(giver.relationship)}
          </Tag>
        </div>

        <div className={styles.contactInfo}>
          {giver.phone && (
            <div className={styles.contactItem}>
              <PhoneOutlined className={styles.contactIcon} />
              {highlightText(giver.phone, currentFilters.name)}
            </div>
          )}
          {giver.email && (
            <div className={styles.contactItem}>
              <MailOutlined className={styles.contactIcon} />
              {highlightText(giver.email, currentFilters.name)}
            </div>
          )}
          {giver.address && (
            <div className={styles.contactItem}>
              <HomeOutlined className={styles.contactIcon} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {giver.address}
              </span>
            </div>
          )}
        </div>



        {giver.social_media && (
          <div className={styles.contactItem}>
            <span style={{ fontSize: 11, color: '#999' }}>Mạng xã hội:</span>
            <span style={{ fontSize: 11 }}>{giver.social_media}</span>
          </div>
        )}

        {giver.notes && (
          <div className={styles.notes}>
            <span style={{ fontSize: 11, color: '#999' }}>Ghi chú:</span>
            <div style={{ fontSize: 11, marginTop: 2 }}>{giver.notes}</div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.mobileShell}>
        {/* Givers List */}
        <main className={styles.giversList}>
          <div className={styles.listHeader}>
            <div className={styles.headerLeft} onClick={() => setFilterModalOpen(true)}>
              <h3>Khách mời</h3>
              <DownOutlined style={{ 
                transform: filterModalOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            </div>
            <div className={styles.headerRight}>
              <div className={styles.addButton} onClick={handleCreate}>
                <PlusOutlined />
              </div>
              <div className={styles.refreshButton} onClick={refreshData} title="Làm mới dữ liệu">
                <ReloadOutlined />
              </div>
              <div className={styles.filterButton} onClick={() => setFilterModalOpen(true)}>
                <FilterOutlined />
                {getFilterCount() > 0 && (
                  <span className={styles.filterBadge}>{getFilterCount()}</span>
                )}
              </div>
            </div>
          </div>

          {/* Scrollable Givers Content */}
          <div className={styles.giversContent} onScroll={handleScroll}>
            {filteredGivers.length === 0 && !loading ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <GiftOutlined />
                </div>
                <div className={styles.emptyText}>
                  {currentFilters.name || currentFilters.relationship || currentFilters.status !== undefined 
                    ? 'Không tìm thấy khách mời nào' 
                    : 'Chưa có khách mời nào'
                  }
                </div>
                <div className={styles.emptySubtext}>
                  {currentFilters.name || currentFilters.relationship || currentFilters.status !== undefined 
                    ? 'Thử thay đổi bộ lọc tìm kiếm'
                    : 'Bắt đầu thêm khách mời đầu tiên'
                  }
                </div>
              </div>
            ) : (
              <>
                {filteredGivers.map(renderGiverCard)}
                {loadingMore && (
                  <div className={styles.loadingMore}>
                    <div className={styles.loadingSpinner}></div>
                    <span>Đang tải thêm...</span>
                  </div>
                )}
                {!hasMore && filteredGivers.length > 0 && (
                  <div className={styles.noMoreData}>
                    <span>Đã tải hết dữ liệu ({filteredGivers.length}/{total} mục)</span>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Detail Modal */}
      <GiftGiversDetailModalMobile
        isOpen={detailModalOpen}
        onClose={handleDetailModalClose}
        giver={selectedGiver}
        onSuccess={handleModalSuccess}
        onDelete={handleModalSuccess}
        onEdit={handleEdit}
      />

      {/* Add/Edit Modal */}
      <AddGiftsGiversModalMobile
        isOpen={addEditModalOpen}
        onClose={handleAddEditModalClose}
        giver={editingGiverForModal}
        mode={addEditModalMode}
        onSuccess={handleModalSuccess}
      />

      {/* Filter Modal */}
      <FilterModalMobile
        isOpen={filterModalOpen}
        onClose={handleFilterModalClose}
        filters={currentFilters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
};

export default GiftGiversPageMobile;
